import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
} from '../components/ui/enhanced-table';
import { useTableSort } from '../hooks/useTableEnhancements';
import { Shield, Plus, Pencil, Trash2, Clock, AlertTriangle, CheckCircle2, Info, Search } from 'lucide-react';
import { toast } from 'sonner';

function formatDuration(minutes) {
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

export default function SLAPoliciesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isRTL = i18n.language === 'ar';
  
  const [policies, setPolicies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    branch_id: '',
    applies_to: 'all',
    category: '',
    response_time_minutes: 60,
    resolution_time_minutes: 480,
    is_active: true,
  });
  
  // Table sorting
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort('sla-policies', 'updated_at', 'desc');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [policiesRes, branchesRes, assetsRes] = await Promise.all([
        api.get('/sla-policies'),
        api.get('/branches'),
        api.get('/assets'),
      ]);
      setPolicies(policiesRes.data);
      setBranches(branchesRes.data);
      
      // Extract unique categories from assets
      const uniqueCategories = [...new Set(assetsRes.data.map(a => a.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = () => {
    setEditingPolicy(null);
    setFormData({
      branch_id: branches[0]?.id || '',
      applies_to: 'all',
      category: '',
      response_time_minutes: 60,
      resolution_time_minutes: 480,
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      branch_id: policy.branch_id,
      applies_to: policy.applies_to,
      category: policy.category || '',
      response_time_minutes: policy.response_time_minutes,
      resolution_time_minutes: policy.resolution_time_minutes,
      is_active: policy.is_active,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (!formData.branch_id) {
        toast.error(t('validation.required'));
        return;
      }
      if (formData.response_time_minutes <= 0 || formData.resolution_time_minutes <= 0) {
        toast.error('SLA times must be positive');
        return;
      }
      if (formData.response_time_minutes >= formData.resolution_time_minutes) {
        toast.error('Response time must be less than resolution time');
        return;
      }

      const payload = {
        ...formData,
        category: formData.category || null,
      };

      if (editingPolicy) {
        await api.put(`/sla-policies/${editingPolicy.id}`, payload);
        toast.success(t('toast.saved'));
      } else {
        await api.post('/sla-policies', payload);
        toast.success(t('toast.saved'));
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save policy:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!policyToDelete) return;
    try {
      await api.delete(`/sla-policies/${policyToDelete.id}`);
      toast.success(t('toast.deleted'));
      setDeleteDialogOpen(false);
      setPolicyToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete policy:', error);
      toast.error(t('common.error'));
    }
  };

  const confirmDelete = (policy) => {
    setPolicyToDelete(policy);
    setDeleteDialogOpen(true);
  };

  // Filter policies
  const filteredPolicies = policies.filter(policy => {
    if (filterBranch && policy.branch_id !== filterBranch) return false;
    if (filterStatus === 'active' && !policy.is_active) return false;
    if (filterStatus === 'inactive' && policy.is_active) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const branchName = policy.branch_name?.toLowerCase() || '';
      const category = policy.category?.toLowerCase() || '';
      if (!branchName.includes(q) && !category.includes(q) && !policy.applies_to.includes(q)) {
        return false;
      }
    }
    return true;
  });

  // Sort policies
  const sortedPolicies = sortData(filteredPolicies);

  const getAppliesToLabel = (appliesTo) => {
    switch (appliesTo) {
      case 'all': return t('sla.all');
      case 'corrective': return t('sla.corrective');
      case 'preventive': return t('sla.preventive');
      default: return appliesTo;
    }
  };

  return (
    <div className="space-y-6" data-testid="sla-policies-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-brand" />
            {t('sla.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{t('sla.subtitle')}</p>
        </div>
        <Button onClick={handleCreate} data-testid="create-sla-policy-btn">
          <Plus className="w-4 h-4 me-2" />
          {t('sla.createPolicy')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="ps-9"
                data-testid="sla-search-input"
              />
            </div>
            <Select value={filterBranch || 'all'} onValueChange={(v) => setFilterBranch(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="sla-branch-filter">
                <SelectValue placeholder={t('common.branch')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[140px]" data-testid="sla-status-filter">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="active">{t('common.active')}</SelectItem>
                <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <EnhancedTable>
          <StickyTableHeader>
            <TableHeader>
              <EnhancedTableRow>
                <SortableHeader column="branch_name" label={t('common.branch')} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader column="applies_to" label={t('sla.appliesTo')} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <EnhancedTableCell isHeader>{t('sla.category')}</EnhancedTableCell>
                <SortableHeader column="response_time_minutes" label={t('sla.responseTime')} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <SortableHeader column="resolution_time_minutes" label={t('sla.resolutionTime')} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                <EnhancedTableCell isHeader>{t('common.status')}</EnhancedTableCell>
                <EnhancedTableCell isHeader className="text-end">{t('common.actions')}</EnhancedTableCell>
              </EnhancedTableRow>
            </TableHeader>
          </StickyTableHeader>
          <EnhancedTableBody>
            {sortedPolicies.length === 0 ? (
              <EnhancedTableRow>
                <EnhancedTableCell colSpan={7}>
                  <TableEmptyState
                    icon={Shield}
                    title={t('sla.noPolicies')}
                    description={t('sla.noPoliciesDesc')}
                  />
                </EnhancedTableCell>
              </EnhancedTableRow>
            ) : (
              sortedPolicies.map(policy => (
                <EnhancedTableRow key={policy.id} data-testid={`sla-policy-row-${policy.id}`}>
                  <EnhancedTableCell className="font-medium">{policy.branch_name}</EnhancedTableCell>
                  <EnhancedTableCell>
                    <Badge variant="outline">{getAppliesToLabel(policy.applies_to)}</Badge>
                  </EnhancedTableCell>
                  <EnhancedTableCell>
                    {policy.category ? (
                      <Badge variant="secondary">{policy.category}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </EnhancedTableCell>
                  <EnhancedTableCell>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-blue-500" />
                      {formatDuration(policy.response_time_minutes)}
                    </div>
                  </EnhancedTableCell>
                  <EnhancedTableCell>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      {formatDuration(policy.resolution_time_minutes)}
                    </div>
                  </EnhancedTableCell>
                  <EnhancedTableCell>
                    {policy.is_active ? (
                      <Badge className="bg-green-500/10 text-green-600 border-green-500/20">{t('common.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('common.inactive')}</Badge>
                    )}
                  </EnhancedTableCell>
                  <EnhancedTableCell className="text-end">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(policy)} data-testid={`edit-policy-${policy.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => confirmDelete(policy)} className="text-red-500 hover:text-red-600" data-testid={`delete-policy-${policy.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </EnhancedTableCell>
                </EnhancedTableRow>
              ))
            )}
          </EnhancedTableBody>
        </EnhancedTable>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="sla-policy-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingPolicy ? t('sla.editPolicy') : t('sla.createPolicy')}
            </DialogTitle>
            <DialogDescription>
              {t('sla.subtitle')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Branch */}
            <div className="space-y-2">
              <Label>{t('common.branch')} *</Label>
              <Select
                value={formData.branch_id}
                onValueChange={(v) => setFormData(prev => ({ ...prev, branch_id: v }))}
                disabled={!!editingPolicy}
              >
                <SelectTrigger data-testid="policy-branch-select">
                  <SelectValue placeholder={t('workRequest.selectBranch')} />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Applies To */}
            <div className="space-y-2">
              <Label>{t('sla.appliesTo')} *</Label>
              <Select
                value={formData.applies_to}
                onValueChange={(v) => setFormData(prev => ({ ...prev, applies_to: v }))}
              >
                <SelectTrigger data-testid="policy-applies-to-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('sla.all')}</SelectItem>
                  <SelectItem value="corrective">{t('sla.corrective')}</SelectItem>
                  <SelectItem value="preventive">{t('sla.preventive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category (Optional) */}
            <div className="space-y-2">
              <Label>{t('sla.categoryOptional')}</Label>
              <Select
                value={formData.category || 'none'}
                onValueChange={(v) => setFormData(prev => ({ ...prev, category: v === 'none' ? '' : v }))}
              >
                <SelectTrigger data-testid="policy-category-select">
                  <SelectValue placeholder={t('common.none')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('common.none')}</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('sla.categoryOptional')} - More specific policies take priority
              </p>
            </div>

            <Separator />

            {/* Response Time */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                {t('sla.responseTime')} ({t('sla.minutes')}) *
              </Label>
              <Input
                type="number"
                min="1"
                value={formData.response_time_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, response_time_minutes: parseInt(e.target.value) || 0 }))}
                data-testid="policy-response-time-input"
              />
              <p className="text-xs text-muted-foreground">{t('sla.responseTimeHelp')}</p>
            </div>

            {/* Resolution Time */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                {t('sla.resolutionTime')} ({t('sla.minutes')}) *
              </Label>
              <Input
                type="number"
                min="1"
                value={formData.resolution_time_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, resolution_time_minutes: parseInt(e.target.value) || 0 }))}
                data-testid="policy-resolution-time-input"
              />
              <p className="text-xs text-muted-foreground">{t('sla.resolutionTimeHelp')}</p>
            </div>

            {/* Pause Note */}
            <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">{t('sla.pauseNote')}</p>
            </div>

            {/* Active Status */}
            <div className="flex items-center justify-between">
              <Label htmlFor="policy-active">{t('common.active')}</Label>
              <Switch
                id="policy-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} data-testid="save-policy-btn">
              {editingPolicy ? t('common.update') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
            <DialogDescription>
              This will permanently delete the SLA policy for {policyToDelete?.branch_name}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-policy-btn">
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
